import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, X, MessageSquare, Loader2 } from 'lucide-react';
import { useChat } from '@/context/ChatContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLocation } from 'react-router-dom';
import { generateAIResponse } from '@/utils/ai';
import { toast } from '@/hooks/use-toast';
import { useSafeWebhook } from '@/hooks/useSafeWebhook';

const ChatBox: React.FC = () => {
  const webhook = useSafeWebhook();
  const { t, language } = useLanguage();
  const { messages, addMessage, isOpen, toggleChat, closeChat } = useChat();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const currentPath = location.pathname;
  const [pastResponses] = useState(new Set<string>());

  const translations = {
    en: {
      aiHelp: "AI Assistant",
      aiHelpIntro: "How can I assist you with the bluetooth$ app?",
      askQuestion: "Ask a question...",
      send: "Send",
      initialMessage: "Hello! I'm your bluetooth$ assistant. I can help you with payment questions, app features, or troubleshooting. What can I help you with today?"
    },
    es: {
      aiHelp: "Asistente IA",
      aiHelpIntro: "¿Cómo puedo ayudarte con la aplicación bluetooth$?",
      askQuestion: "Hacer una pregunta...",
      send: "Enviar",
      initialMessage: "¡Hola! Soy tu asistente de bluetooth$. Puedo ayudarte con preguntas sobre pagos, funciones de la aplicación o solución de problemas. ¿En qué puedo ayudarte hoy?"
    },
    fr: {
      aiHelp: "Assistant IA",
      aiHelpIntro: "Comment puis-je vous aider avec l'application bluetooth$?",
      askQuestion: "Poser une question...",
      send: "Envoyer",
      initialMessage: "Bonjour! Je suis votre assistant bluetooth$. Je peux vous aider avec des questions sur les paiements, les fonctionnalités de l'application ou le dépannage. Comment puis-je vous aider aujourd'hui?"
    }
  };

  const tx = translations[language as keyof typeof translations] || translations.en;

  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage({ role: 'assistant', content: tx.initialMessage });
    }
  }, [isOpen, messages.length, addMessage, tx.initialMessage]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim()) return;

    const userMessage = { role: 'user' as const, content: input };
    addMessage(userMessage);
    setInput('');
    setIsLoading(true);

    try {
      // Log the incoming request for debugging
      console.log(`AI Request: "${userMessage.content}" on path "${currentPath}"`);
      
      const response = await generateAIResponse(userMessage.content, currentPath, language);
      
      // Track if this is a repeated response
      const isRepeated = pastResponses.has(response);
      if (isRepeated) {
        console.log('Warning: Repeated AI response detected');
      }
      pastResponses.add(response);
      
      // Log the AI response for debugging
      console.log(`AI Response: "${response.substring(0, 50)}${response.length > 50 ? '...' : ''}"`);
      
      addMessage({ role: 'assistant', content: response });
    } catch (error) {
      console.error('Error generating AI response:', error);
      addMessage({ 
        role: 'assistant', 
        content: language === 'es' 
          ? 'Lo siento, tuve un problema al procesar tu solicitud. Por favor, intenta nuevamente.'
          : language === 'fr'
          ? 'Désolé, j\'ai eu un problème à traiter votre demande. Veuillez réessayer.'
          : 'Sorry, I had an issue processing your request. Please try again.' 
      });
      
      toast({
        title: 'Error',
        description: 'There was a problem generating a response',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 flex items-center gap-3 z-50">
        <div className="text-sm text-gray-600 bg-white shadow-md rounded-lg px-4 py-2 max-w-xs">
          <p className="line-clamp-2">{tx.initialMessage.split('.')[0] + '.'}</p>
        </div>
        <Button
          onClick={toggleChat}
          className="rounded-full w-12 h-12 p-0 flex items-center justify-center shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 z-50"
          aria-label="Open chat"
        >
          <MessageSquare size={24} />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-80 sm:w-96 h-[500px] max-h-[80vh] bg-background border border-border rounded-lg shadow-xl flex flex-col z-50">
      <div className="p-3 border-b flex items-center justify-between bg-muted/50">
        <div className="flex items-center gap-2">
          <Bot size={20} />
          <h3 className="font-medium">{tx.aiHelp}</h3>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={closeChat}
          aria-label="Close chat"
        >
          <X size={18} />
        </Button>
      </div>

      <ScrollArea className="flex-1 p-3" ref={scrollAreaRef}>
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`rounded-lg px-3 py-2 max-w-[85%] ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}
              >
                {message.content}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="rounded-lg px-3 py-2 bg-muted flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">
                  {language === 'es' 
                    ? 'Pensando...' 
                    : language === 'fr'
                    ? 'Réflexion...' 
                    : 'Thinking...'}
                </span>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      <form onSubmit={handleSendMessage} className="p-3 border-t flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={tx.askQuestion}
          className="flex-1 px-3 py-2 text-sm rounded-md border border-input bg-background"
          disabled={isLoading}
        />
        <Button
          type="submit"
          size="icon"
          disabled={isLoading || !input.trim()}
          className="h-9 w-9"
          aria-label={tx.send}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </form>
    </div>
  );
};

export default ChatBox;
